import { IAsyncActionCreator, createAsyncActionCreator } from '../store/actions/createActionCreator';
import { Delete, graft } from '../utilities/Graft';
import { mapObject, makeObject } from '../utilities/objects';
import { IFacet, pack, IFaceted } from '../models/Facet';
import { KeyFacet, stringPart, Key } from '../models/KeyFacet';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../store/actions/ItemCacheActions';
import { updateItems } from '../store/reducers/itemMapReducer';
import { invalidationFacet, resolvedFacet, DemandResolution } from '../facets/DemandFacets';
import { readItems } from './readItems';
import { IItemCache } from '../models/IItemCacheObserver';

/**
 * @public
 */
export interface IInvalidateItemsPayload {
    items: {
        [itemKey: string]: IFacet<{}>[];
    };
}

const invalidationKeyFacet = new KeyFacet<{
    itemKey: string;
    facetId: string;
}>('invalidation', {
    itemKey: stringPart(),
    facetId: stringPart()
});

/**
 * Creates an action which, when dispatched, applies the given item updates to the cache and marks
 * every mentioned facet as resolved.
 * @public
 */
export const invalidateItems: IAsyncActionCreator<IInvalidateItemsPayload, IItemCacheThunkAction<void>> =
    createAsyncActionCreator((payload: IInvalidateItemsPayload): IItemCacheThunkAction<void> => {
        const {
            items
        } = payload;

        return (dispatch: IItemCacheDispatch) => {
            dispatch(updateItems({
                items: dispatch(readItems((itemCache: IItemCache) => {
                    // Build a queue of items and their facets to be invalidated.
                    const pendingInvalidations: Key<typeof invalidationKeyFacet.type>[] = [];
                    // Keep track of all items and facets seen to invalidate.
                    const allInvalidations: {
                        [invalidationKey: string]: boolean;
                    } = {};

                    const invalidateItemsPayloadItems: {
                        [itemKey: string]: {
                            [facetId: string]: boolean;
                        };
                    } = {};

                    const queueInvalidation = (itemKey: string, facetId: string): void => {
                        const invalidationKey = invalidationKeyFacet.serialize({
                            itemKey: itemKey,
                            facetId: facetId
                        });

                        if (!allInvalidations[invalidationKey]) {
                            allInvalidations[invalidationKey] = true;
                            pendingInvalidations.push(invalidationKey);

                            if (!invalidateItemsPayloadItems[itemKey]) {
                                invalidateItemsPayloadItems[itemKey] = {};
                            }

                            invalidateItemsPayloadItems[itemKey][facetId] = true;
                        }
                    };

                    const invalidatedItems: (typeof updateItems.payloadType)['items'] = {};

                    for (const itemKey in items) {
                        if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
                            const facets = items[itemKey];

                            for (const facet of facets) {
                                queueInvalidation(itemKey, facet.id);
                            }
                        }
                    }

                    while (pendingInvalidations.length > 0) {
                        const {
                            itemKey,
                            facetId
                        } = invalidationKeyFacet.deserialize(pendingInvalidations.pop()!);

                        const invalidation = itemCache.demandItemFacet(invalidationFacet, itemKey);

                        if (invalidation) {
                            const trigger = invalidation.triggers[facetId];

                            if (trigger) {
                                const {
                                    targets
                                } = trigger;

                                for (const targetItemKey in targets) {
                                    if (Object.prototype.hasOwnProperty.call(targets, targetItemKey)) {
                                        const targetFacetIds = targets[targetItemKey];

                                        for (const targetFacetId in targetFacetIds) {
                                            if (Object.prototype.hasOwnProperty.call(targetFacetIds, targetFacetId)) {
                                                if (targetFacetIds[targetFacetId]) {
                                                    queueInvalidation(targetItemKey, targetFacetId);
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    for (const itemKey in invalidateItemsPayloadItems) {
                        if (Object.prototype.hasOwnProperty.call(invalidateItemsPayloadItems, itemKey)) {
                            const facetIds = invalidateItemsPayloadItems[itemKey];

                            invalidatedItems[itemKey] = pack({
                                resolved: resolvedFacet
                            }, {
                                    resolved: {
                                        demands: mapObject(facetIds, () => new Delete<DemandResolution>())
                                    }
                                });
                        }
                    }

                    return invalidatedItems;
                }))
            }));
        };
    });

/**
 * @public
 */
export interface IFacetTrigger<T> {
    /**
     * Sets up the target item's facets to be invalidated
     * when the packed item's trigger facets are invalidated.
     */
    thenInvalidate(itemKey: string, ...facets: IFacet<{}>[]): T;
}

/**
 * @public
 */
export interface IInvalidationFacetBuilder {
    /**
     * Sets up a trigger for a facet on the item being packed.
     */
    forFacet(facet: IFacet<{}>): IFacetTrigger<this>;
    /**
     * Packs the value for the invalidation facet.
     */
    pack(): IFaceted;
}

/**
 * Creates a helper for packing an invalidation facet.
 * @public
*/
export function getInvalidationFacet(): IInvalidationFacetBuilder {
    interface IInvalidationBuilder extends IInvalidationFacetBuilder {
        value: typeof invalidationFacet.type;
    }

    const invalidation: IInvalidationBuilder = {
        value: {
            triggers: pack({}, {})
        },
        forFacet(facet: IFacet<{}>): IFacetTrigger<IInvalidationBuilder> {
            return {
                thenInvalidate: (itemKey: string, ...facets: IFacet<{}>[]) => {
                    return graft(this, {
                        value: {
                            triggers: {
                                [facet.id]: {
                                    targets: {
                                        [itemKey]: makeObject(facets.map((targetFacet: IFacet<{}>) => targetFacet.id), () => true)
                                    }
                                }
                            }
                        }
                    });
                }
            };
        },
        pack(): IFaceted {
            return invalidationFacet.pack(this.value);
        }
    };

    return invalidation;
}
